// src/pages/administrasi/AdmDashboard.jsx
//
// The landing page of Sekretaris and Bendahara. Everything on it is the state
// of THE active event (every role works in the same single one, so there is no
// picker), fetched in a single call to /api/administrasi/dashboard/. The
// backend only sends the blocks the user's module allows, so this just renders
// whatever comes back.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../../utils/config";
import MiniTable from "../../components/MiniTable";

const token = () => localStorage.getItem("accessToken");

async function apiFetch(url, opts = {}) {
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}`, ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.detail || "Permintaan gagal.");
  }
  return res.status === 204 ? null : res.json();
}

const fmtRp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const fmtWhen = (iso) =>
  iso
    ? new Date(iso)
        .toLocaleString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
        .toUpperCase()
    : "-";

const CATEGORY = {
  surat_keluar: { label: "Surat Keluar", path: "surat-keluar" },
  surat_masuk: { label: "Surat Masuk", path: "surat-masuk" },
  form_pengajuan: { label: "Form Pengajuan", path: "form-pengajuan" },
};

const PROBLEM = {
  FAILED: { label: "Gagal", cls: "badge-danger" },
  MISSING: { label: "File hilang", cls: "badge-warning" },
  DRAFT: { label: "Draft menggantung", cls: "badge-info" },
};

const STATUS = {
  DRAFT: { label: "Draft", cls: "badge-info" },
  GENERATED: { label: "Selesai", cls: "badge-success" },
  FAILED: { label: "Gagal", cls: "badge-danger" },
  MISSING: { label: "File hilang", cls: "badge-warning" },
};

function Head({ title, note, to, linkLabel }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <div className="eyebrow">{title}</div>
      {to ? (
        <Link to={to} className="db-link">
          {linkLabel}
        </Link>
      ) : (
        note && <div className="text-[13px]" style={{ color: "var(--text-dim)" }}>{note}</div>
      )}
    </div>
  );
}

function Tile({ group, title, value, note, led, money }) {
  return (
    <div className="db-tile">
      <div className="flex items-center justify-between">
        <span className="db-tile-group">{group}</span>
        {/* led: "" = normal (green), "is-warn" / "is-alert", undefined = off */}
        <span className={`db-led ${led === undefined ? "is-off" : led}`} aria-hidden="true" />
      </div>
      <div className="flex flex-col gap-1.5">
        <span className={`db-count${money ? " is-money" : ""}`}>{value}</span>
        <span className="text-sm font-medium">{title}</span>
      </div>
      <div className="text-[13px]" style={{ color: "var(--text-dim)" }}>{note}</div>
    </div>
  );
}

function Notice({ children }) {
  return (
    <div className="db-card" style={{ borderColor: "color-mix(in srgb, var(--amber) 45%, transparent)" }}>
      <span className="text-sm" style={{ color: "var(--amber)" }}>{children}</span>
    </div>
  );
}

function FolderNotice({ folder, name }) {
  if (folder.verified) return null;
  return (
    <Notice>
      {folder.set
        ? `Folder Drive ${name} untuk event ini belum diverifikasi.`
        : `Folder Drive ${name} untuk event ini belum diisi, jadi file belum bisa diunggah.`}{" "}
      <Link to="/administrasi/folder-config" className="underline">
        Atur di Event &amp; Folder
      </Link>
    </Notice>
  );
}

/* ───────────────────────────── Bendahara ───────────────────────────── */

function BendaharaBlock({ d, eventName, onRepush, busyKey, actionError }) {
  const saldoNegative = d.saldo < 0;
  const needsAction =
    d.over_budget_count > 0 || d.belum_drive_count > 0 || !d.folder.verified;

  return (
    <div className="flex flex-col gap-9">
      <section className="db-section">
        <Head title={`BENDAHARA · ${eventName}`} />
        <div className="db-tiles">
          <Tile
            group="ANGGARAN" title="Total RAB" money value={fmtRp(d.anggaran)}
            note={d.rab_count ? `${d.rab_count} pos anggaran` : "Belum ada anggaran"}
            led={d.rab_count ? "" : "is-off"}
          />
          <Tile
            group="KAS MASUK" title="Sudah cair" money value={fmtRp(d.kas_masuk)}
            note={d.anggaran ? `${d.pct_kas_masuk}% dari anggaran` : "Belum ada anggaran"}
            led={d.kas_masuk ? "" : "is-off"}
          />
          <Tile
            group="REALISASI" title="Sudah terpakai" money value={fmtRp(d.realisasi)}
            note={d.anggaran ? `${d.pct_realisasi}% dari anggaran` : "Belum ada anggaran"}
            led={d.over_budget_count ? "is-warn" : d.realisasi ? "" : "is-off"}
          />
          <Tile
            group="SALDO KAS" title="Kas masuk − realisasi" money value={fmtRp(d.saldo)}
            note={`Sisa anggaran ${fmtRp(d.sisa_anggaran)}`}
            led={saldoNegative ? "is-alert" : ""}
          />
        </div>
      </section>

      <section className="db-section">
        <Head title="RENCANA VS REALISASI" to="/administrasi/bendahara/anggaran" linkLabel="Buka anggaran →" />
        {d.sections.length === 0 ? (
          <div className="glass-table-wrap">
            <p className="db-empty">Belum ada anggaran (RAB) atau realisasi untuk event ini.</p>
          </div>
        ) : (
          <div className="db-card">
            {d.sections.map((s) => {
              const over = s.realisasi > s.rencana;
              const pct = s.rencana > 0
                ? Math.min(100, Math.round((s.realisasi / s.rencana) * 100))
                : s.realisasi > 0 ? 100 : 0;
              return (
                <div key={s.section || "_"} className="flex flex-col gap-2">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <span className="text-sm font-semibold">{s.section || "Tanpa seksi"}</span>
                    <span className="font-mono text-xs" style={{ color: "var(--text-secondary)" }}>
                      {fmtRp(s.realisasi)} / {fmtRp(s.rencana)}
                      {over && (
                        <span style={{ color: "var(--danger)" }}> · lebih {fmtRp(s.realisasi - s.rencana)}</span>
                      )}
                    </span>
                  </div>
                  <div className={`db-bar${over ? " is-over" : ""}`}>
                    <span style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="db-section">
        <Head title="PERLU TINDAKAN" />
        {!needsAction && d.unrealized_count === 0 ? (
          <div className="glass-table-wrap">
            <p className="db-empty">Tidak ada yang perlu ditindak.</p>
          </div>
        ) : (
          <div className="db-grid-wide">
            {d.over_budget_count > 0 && (
              <div className="db-card">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[15px] font-semibold">Melebihi anggaran</span>
                  <span className="tb-count is-alert">{d.over_budget_count}</span>
                </div>
                <MiniTable
                  className="-mx-5 -mb-5"
                  cols={[
                    { key: "pos", label: "Pos" },
                    { key: "realisasi", label: "Realisasi", right: true, shrink: true },
                    { key: "lebih", label: "Lebih", right: true, shrink: true },
                  ]}
                  rows={d.over_budget.map((r) => ({
                    pos: (
                      <span className="inline-flex flex-col">
                        <span className="font-semibold">{r.uraian}</span>
                        <span className="db-code">
                          {[r.section, r.kode].filter(Boolean).join(" · ")}
                          {" · rencana "}
                          {fmtRp(r.rencana)}
                        </span>
                      </span>
                    ),
                    realisasi: fmtRp(r.realisasi),
                    lebih: <span className="font-bold" style={{ color: "var(--danger)" }}>+{fmtRp(r.selisih)}</span>,
                  }))}
                />
              </div>
            )}

            {d.belum_drive_count > 0 && (
              <div className="db-card">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[15px] font-semibold">Belum terunggah ke Drive</span>
                  <span className="tb-count is-alert">{d.belum_drive_count}</span>
                </div>
                {actionError && <div className="note-danger">{actionError}</div>}
                <div className="flex flex-col">
                  {d.belum_drive.map((r) => {
                    const key = `${r.kind}-${r.id}`;
                    return (
                      <div
                        key={key}
                        className="flex items-center justify-between gap-3 py-2.5 border-t border-[var(--border-subtle)] first:border-t-0"
                      >
                        <div className="min-w-0 flex flex-col">
                          <span className="text-sm font-semibold truncate">{r.label}</span>
                          <span className="db-code">
                            {r.kind === "kas_masuk" ? "KAS MASUK" : "KAS KELUAR"} · {fmtRp(r.nominal)}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => onRepush(r)}
                          disabled={busyKey === key}
                          className="btn-outline btn-sm shrink-0"
                        >
                          {busyKey === key ? "Mengunggah…" : "Unggah ulang"}
                        </button>
                      </div>
                    );
                  })}
                </div>
                {d.belum_drive_count > d.belum_drive.length && (
                  <span className="text-[13px]" style={{ color: "var(--text-dim)" }}>
                    +{d.belum_drive_count - d.belum_drive.length} lainnya di halaman Kas Masuk / Realisasi Nota.
                  </span>
                )}
              </div>
            )}

            {d.unrealized_count > 0 && (
              <div className="db-card !flex-row !items-center !justify-between">
                <div className="flex flex-col gap-1.5 min-w-0">
                  <span className="text-[15px] font-semibold">Pos belum ada realisasi</span>
                  <span className="text-[13px]" style={{ color: "var(--text-dim)" }}>
                    {d.unrealized_count} dari {d.rab_count} pos anggaran belum punya nota.
                  </span>
                </div>
                <Link to="/administrasi/bendahara/realisasi-nota" className="btn-outline btn-sm shrink-0">
                  Input nota
                </Link>
              </div>
            )}

            <FolderNotice folder={d.folder} name="Bendahara" />
          </div>
        )}
      </section>

      <section className="db-section">
        <Head title="AKTIVITAS TERBARU" to="/administrasi/bendahara/kas-keluar" linkLabel="Lihat rekap →" />
        <div className="glass-table-wrap">
          {d.recent.length === 0 ? (
            <p className="db-empty">Belum ada kas masuk atau realisasi untuk event ini.</p>
          ) : (
            <MiniTable
              cols={[
                { key: "kind", label: "Jenis", shrink: true },
                { key: "label", label: "Keterangan" },
                { key: "nominal", label: "Nominal", right: true, shrink: true },
                { key: "by", label: "Oleh", shrink: true },
                { key: "time", label: "Waktu", right: true, shrink: true, mono: true, muted: true },
              ]}
              rows={d.recent.map((r) => ({
                kind: (
                  <span className={`badge ${r.kind === "masuk" ? "badge-success" : "badge-danger"}`}>
                    {r.kind === "masuk" ? "MASUK" : "KELUAR"}
                  </span>
                ),
                label: r.label,
                nominal: <span className="tabular-nums font-semibold">{fmtRp(r.nominal)}</span>,
                by: r.by || "-",
                time: fmtWhen(r.created_at),
              }))}
            />
          )}
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────── Sekretaris ───────────────────────────── */

function SekretarisBlock({ d, eventName }) {
  const link = (cat) => `/administrasi/sekretaris/${CATEGORY[cat].path}`;
  return (
    <div className="flex flex-col gap-9">
      <section className="db-section">
        <Head title={`SEKRETARIS · ${eventName}`} />
        <div className="db-tiles">
          {["surat_keluar", "surat_masuk", "form_pengajuan"].map((cat) => (
            <Tile
              key={cat}
              group="DOKUMEN"
              title={CATEGORY[cat].label}
              value={d.counts[cat] ?? 0}
              note={d.counts[cat] ? "untuk event ini" : "Belum ada"}
              led={d.counts[cat] ? "" : "is-off"}
            />
          ))}
          <Tile
            group="PERHATIAN" title="Bermasalah" value={d.problem_count}
            note={d.problem_count ? "Gagal, file hilang, atau draft menggantung" : "Semua aman"}
            led={d.problem_count ? "is-alert" : ""}
          />
        </div>
        <div className="flex flex-wrap gap-2.5">
          <Link to={link("surat_keluar")} className="btn-primary">+ Surat Keluar</Link>
          <Link to={link("form_pengajuan")} className="btn-ghost">+ Form Pengajuan</Link>
          <Link to={link("surat_masuk")} className="btn-ghost">Surat Masuk</Link>
          <Link to="/administrasi/sekretaris/arsip" className="btn-ghost">Arsip</Link>
        </div>
      </section>

      <section className="db-section">
        <Head title="PERLU TINDAKAN" />
        {d.problem_count === 0 && d.folder.verified ? (
          <div className="glass-table-wrap">
            <p className="db-empty">Tidak ada yang perlu ditindak.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {d.problem_count > 0 && (
              <div className="glass-table-wrap">
                <MiniTable
                  cols={[
                    { key: "status", label: "Status", shrink: true },
                    { key: "doc", label: "Dokumen" },
                    { key: "info", label: "Keterangan" },
                    { key: "by", label: "Oleh", shrink: true, muted: true },
                    { key: "time", label: "Waktu", right: true, shrink: true, mono: true, muted: true },
                    { key: "go", label: "", right: true, shrink: true },
                  ]}
                  rows={d.problems.map((p) => {
                    const st = PROBLEM[p.status] || PROBLEM.DRAFT;
                    return {
                      status: <span className={`badge ${st.cls}`}>{st.label}</span>,
                      doc: (
                        <span className="inline-flex flex-col">
                          <span className="font-semibold">{p.template_name}</span>
                          <span className="db-code">{CATEGORY[p.category]?.label || p.category}</span>
                        </span>
                      ),
                      info:
                        p.status === "FAILED"
                          ? p.error_message || "Generate gagal."
                          : p.status === "MISSING"
                          ? "File dihapus — ada di tempat sampah Drive."
                          : "Belum digenerate lebih dari 24 jam.",
                      by: p.by || "-",
                      time: fmtWhen(p.created_at),
                      go: CATEGORY[p.category] ? (
                        <Link to={link(p.category)} className="db-link">Buka →</Link>
                      ) : null,
                    };
                  })}
                />
                {d.problem_count > d.problems.length && (
                  <p className="db-empty">+{d.problem_count - d.problems.length} lainnya di halaman kategorinya.</p>
                )}
              </div>
            )}
            <FolderNotice folder={d.folder} name="Sekretaris" />
          </div>
        )}
      </section>

      <section className="db-section">
        <Head title="AKTIVITAS TERBARU" to="/administrasi/sekretaris/arsip" linkLabel="Buka arsip →" />
        <div className="glass-table-wrap">
          {d.recent.length === 0 ? (
            <p className="db-empty">Belum ada surat atau pengajuan untuk event ini.</p>
          ) : (
            <MiniTable
              cols={[
                { key: "doc", label: "Dokumen" },
                { key: "status", label: "Status", shrink: true },
                { key: "by", label: "Oleh", shrink: true },
                { key: "time", label: "Waktu", right: true, shrink: true, mono: true, muted: true },
                { key: "open", label: "", right: true, shrink: true },
              ]}
              rows={d.recent.map((r) => {
                const st = STATUS[r.status] || STATUS.DRAFT;
                return {
                  doc: (
                    <span className="inline-flex flex-col">
                      <span className="font-semibold">{r.template_name}</span>
                      <span className="db-code">{CATEGORY[r.category]?.label || r.category}</span>
                    </span>
                  ),
                  status: <span className={`badge ${st.cls}`}>{st.label}</span>,
                  by: r.by || "-",
                  time: fmtWhen(r.created_at),
                  open: r.drive_file_url ? (
                    <a href={r.drive_file_url} target="_blank" rel="noreferrer" className="db-link">Buka →</a>
                  ) : null,
                };
              })}
            />
          )}
        </div>
      </section>
    </div>
  );
}

/* ───────────────────────────── the page ───────────────────────────── */

export default function AdmDashboard({ primary }) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const [actionError, setActionError] = useState("");

  const load = useCallback(() => {
    setError("");
    return apiFetch("/api/administrasi/dashboard/")
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Retry a Drive push for an entry whose first upload failed (the file is
  // already stored on the server, nothing is re-attached).
  const repush = async (row) => {
    const key = `${row.kind}-${row.id}`;
    const path = row.kind === "kas_masuk" ? "kas-masuk" : "kas-keluar";
    setActionError("");
    setBusyKey(key);
    try {
      await apiFetch(`/api/administrasi/${path}/${row.id}/repush-drive/`, { method: "POST" });
      load();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusyKey(null);
    }
  };

  if (loaded && data && data.event === null) {
    return (
      <div className="db-card !flex-row !items-center !justify-between !gap-4 flex-wrap">
        <div className="flex flex-col gap-1">
          <span className="text-[15px] font-semibold">Belum ada event aktif</span>
          <span className="text-[13px]" style={{ color: "var(--text-dim)" }}>
            Mulai event dulu supaya surat, anggaran, dan kas bisa dikerjakan.
          </span>
        </div>
        <Link to="/administrasi/folder-config" className="btn-primary">
          Mulai event
        </Link>
      </div>
    );
  }

  const eventName = data?.event?.name || "";
  const blocks = {
    bendahara: data?.bendahara && (
      <BendaharaBlock
        key="bendahara"
        d={data.bendahara}
        eventName={eventName}
        onRepush={repush}
        busyKey={busyKey}
        actionError={actionError}
      />
    ),
    sekretaris: data?.sekretaris && <SekretarisBlock key="sekretaris" d={data.sekretaris} eventName={eventName} />,
  };
  // The role's own module first, the extra one (from a second kelompok) after.
  const order = primary === "sekretaris" ? ["sekretaris", "bendahara"] : ["bendahara", "sekretaris"];

  return (
    <div className="flex flex-col gap-9">
      <div className="db-card !flex-row !items-center !justify-between !gap-4 flex-wrap">
        <div className="flex flex-col gap-1 min-w-0">
          <span className="eyebrow">EVENT AKTIF</span>
          <span className="text-lg font-semibold truncate">{eventName || "…"}</span>
        </div>
        <Link to="/administrasi/folder-config" className="btn-outline btn-sm">
          Kelola event
        </Link>
      </div>

      {error && <div className="note-danger">{error}</div>}
      {!loaded && <p className="text-sm text-muted">Memuat…</p>}

      {data?.event && <div className="flex flex-col gap-12">{order.map((m) => blocks[m])}</div>}
    </div>
  );
}
